import { useMemo, useState } from 'react';
import { useUploads } from '@/api';
import {
  Badge,
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/atoms';
import { isImageUpload } from '@/lib/helpers/product-images';
import { cn } from '@/lib/utils';
import type { Upload } from '@/types';
import { CheckIcon, ChevronDown, ImageIcon } from 'lucide-react';

interface MediaLibraryMultiSelectProps {
  value: number[];
  onChange: (value: number[]) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

function getUploadLabel(upload: Upload): string {
  const filename = upload.metaData?.filename;
  return filename ? `${filename} (ID ${upload.id})` : `Image ID ${upload.id}`;
}

export function MediaLibraryMultiSelect({
  value,
  onChange,
  disabled = false,
  placeholder = 'Attach images from library',
  className,
}: MediaLibraryMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { data: uploads = [], isLoading } = useUploads();

  const imageUploads = useMemo(
    () =>
      uploads.filter((upload) =>
        isImageUpload(upload.metaData?.mimetype, upload.metaData?.filename)
      ),
    [uploads]
  );

  const filteredUploads = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return imageUploads;

    return imageUploads.filter((upload) => {
      const label = getUploadLabel(upload).toLowerCase();
      return (
        label.includes(query) ||
        String(upload.id).includes(query) ||
        upload.url.toLowerCase().includes(query)
      );
    });
  }, [imageUploads, search]);

  const selectedUploads = imageUploads.filter((upload) =>
    value.includes(upload.id)
  );

  const toggleUpload = (uploadId: number) => {
    if (value.includes(uploadId)) {
      onChange(value.filter((id) => id !== uploadId));
      return;
    }
    onChange([...value, uploadId]);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setSearch('');
  };

  return (
    <div className={cn('space-y-2', className)}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild disabled={disabled}>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'border-input flex h-10 w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3.5 py-2.5 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
              className
            )}
          >
            <span
              className={cn(
                'truncate text-left',
                selectedUploads.length === 0 && 'text-muted-foreground'
              )}
            >
              {selectedUploads.length > 0
                ? `${selectedUploads.length} image${selectedUploads.length === 1 ? '' : 's'} selected`
                : placeholder}
            </span>
            <ChevronDown className="size-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[min(24rem,calc(100vw-2rem))] p-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div className="border-b border-border p-2">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name or ID..."
              className="h-9"
              autoFocus
            />
          </div>
          <div className="max-h-72 overflow-y-auto p-2">
            {isLoading ? (
              <p className="text-muted-foreground px-2 py-6 text-center text-sm">
                Loading media library...
              </p>
            ) : filteredUploads.length === 0 ? (
              <p className="text-muted-foreground px-2 py-6 text-center text-sm">
                No images in your library yet. Upload images in the Gallery tab
                first.
              </p>
            ) : (
              filteredUploads.map((upload) => {
                const isSelected = value.includes(upload.id);

                return (
                  <button
                    key={upload.id}
                    type="button"
                    className={cn(
                      'hover:bg-primary/5 flex w-full items-center gap-3 rounded-md p-2 text-left',
                      isSelected && 'bg-primary/5'
                    )}
                    onClick={() => toggleUpload(upload.id)}
                  >
                    <span
                      className={cn(
                        'flex size-4 shrink-0 items-center justify-center rounded border',
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input bg-background'
                      )}
                    >
                      {isSelected ? <CheckIcon className="size-3" /> : null}
                    </span>
                    <img
                      src={upload.url}
                      alt={upload.metaData?.filename || `Upload ${upload.id}`}
                      className="size-10 rounded-md border object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {upload.metaData?.filename || `Image ${upload.id}`}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        ID {upload.id}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
          {selectedUploads.length > 0 && (
            <div className="border-t border-border p-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => onChange([])}
              >
                Clear selection
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {selectedUploads.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedUploads.map((upload) => (
            <div
              key={upload.id}
              className="flex items-center gap-2 rounded-md border bg-muted/20 px-2 py-1"
            >
              <img
                src={upload.url}
                alt={upload.metaData?.filename || `Upload ${upload.id}`}
                className="size-8 rounded object-cover"
              />
              <Badge variant="outline">ID {upload.id}</Badge>
            </div>
          ))}
        </div>
      )}

      {selectedUploads.length === 0 && (
        <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <ImageIcon className="size-3.5" />
          Select one or more images from your media library.
        </p>
      )}
    </div>
  );
}
